import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { DIVISIONS } from "../data/divisions";

/**
 * Divisioni — pagina index che elenca tutte le 8 divisioni operative del LSSD.
 * Ogni card è cliccabile e porta a /divisioni/:slug con descrizione dettagliata.
 */
export default function Divisioni() {
  return (
    <div data-testid="divisioni-index-page" className="bg-[var(--lssd-olive-deep)]">

      {/* ============ HERO ============ */}
      <section className="border-b border-[var(--lssd-khaki)]/25 bg-[var(--lssd-olive-dark)]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono mb-6" data-testid="divisioni-breadcrumb">
            <Link to="/" className="text-[var(--lssd-khaki)]/70 hover:text-[var(--lssd-khaki)] transition-colors">Home</Link>
            <ChevronRight className="h-3 w-3 text-[var(--lssd-khaki)]/40" />
            <span className="text-[var(--lssd-khaki)] font-semibold">Divisioni</span>
          </div>

          <div className="kicker mb-3">Le divisioni operative</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[var(--lssd-cream)] tracking-tight leading-[1.02] max-w-4xl">
            Otto unità, <span className="text-[var(--lssd-khaki)]">una sola missione</span>.
          </h1>
          <p className="mt-6 text-[var(--lssd-cream)]/75 text-lg leading-relaxed max-w-2xl">
            Il LSSD opera attraverso divisioni specializzate, ciascuna con competenze
            specifiche per coprire ogni aspetto della sicurezza pubblica della Contea.
            Clicca su una divisione per scoprirne missione, responsabilità e struttura.
          </p>
        </div>
      </section>

      {/* ============ GRID DIVISIONI ============ */}
      <section className="py-16 sm:py-20 bg-[var(--lssd-olive-deep)]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {DIVISIONS.map((d) => {
              const Icon = d.icon;
              return (
                <Link
                  key={d.code}
                  to={`/divisioni/${d.slug}`}
                  data-testid={`divisioni-card-${d.code}`}
                  className="group relative bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/20 rounded-2xl p-7 hover:border-[var(--lssd-khaki)]/60 hover:-translate-y-0.5 transition-all overflow-hidden"
                >
                  {/* accent oro laterale */}
                  <div className="absolute left-0 top-6 bottom-6 w-[3px] bg-[var(--lssd-khaki)]/0 group-hover:bg-[var(--lssd-khaki)] transition-colors rounded-r-full" />

                  <div className="h-12 w-12 rounded-full bg-[var(--lssd-khaki)]/12 border border-[var(--lssd-khaki)]/35 flex items-center justify-center mb-4 group-hover:bg-[var(--lssd-khaki)]/22 transition-colors">
                    <Icon className="h-5 w-5 text-[var(--lssd-khaki)]" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-khaki)] mb-2">
                    Unit · {d.code}
                  </div>
                  <h3 className="font-display font-bold text-xl text-[var(--lssd-cream)] mb-2 leading-tight">
                    {d.name}
                  </h3>
                  <p className="text-[14px] text-[var(--lssd-cream)]/70 leading-relaxed mb-4 min-h-[64px]">
                    {d.short}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {d.tags.slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-[0.1em] font-semibold px-2 py-0.5 rounded-full bg-[var(--lssd-olive-deep)] border border-[var(--lssd-khaki)]/20 text-[var(--lssd-khaki)]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-bold text-[var(--lssd-khaki)]">
                    Approfondisci <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>

        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="bg-[var(--lssd-olive-dark)] border-t border-[var(--lssd-khaki)]/20 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="kicker mb-3">Vuoi entrare?</div>
          <h2 className="font-display font-black text-2xl sm:text-4xl text-[var(--lssd-cream)] tracking-tight mb-4">
            Ogni divisione parte dalla <span className="text-[var(--lssd-khaki)]">Patrol</span>.
          </h2>
          <p className="text-[var(--lssd-cream)]/70 text-[15px] mb-8 max-w-xl mx-auto leading-relaxed">
            Ogni operatore inizia come Vice Sceriffo in Patrol. Solo dopo l&apos;Accademia
            e i primi mesi di servizio potrai richiedere un trasferimento in una delle
            divisioni specialistiche.
          </p>
          <Link to="/arruolamento" data-testid="divisioni-cta-arruolamento">
            <span className="btn-gold">Candidati Ora <ArrowRight className="h-4 w-4" /></span>
          </Link>
        </div>
      </section>
    </div>
  );
}
